const LEVEL_CLASS_MAP: Record<string, string> = {
  Beginner: 'preview-spans preview-span-yellow',
  Intermediate: 'preview-spans preview-span-orange',
  Fluent: 'preview-spans preview-span-green',
  Native: 'preview-spans preview-span-red',
};

export const LANGUAGE_LEVELS = ['Beginner', 'Intermediate', 'Fluent', 'Native'] as const;

export type LanguageLevel = (typeof LANGUAGE_LEVELS)[number];

export const LANGUAGE_LEVEL_META: Record<
  LanguageLevel,
  { color: string; progress: number; modalClass: string }
> = {
  Beginner: {
    color: '#6f42c1',
    progress: 30,
    modalClass: 'lang-level-beginner',
  },
  Intermediate: {
    color: '#fd7e14',
    progress: 60,
    modalClass: 'lang-level-intermediate',
  },
  Fluent: { color: '#0d6efd', progress: 90, modalClass: 'lang-level-fluent' },
  Native: { color: '#198754', progress: 100, modalClass: 'lang-level-native' },
};

const LANGUAGE_REGIONS: Record<string, { code: string; region: string }> = {
  English: { code: 'EN', region: 'Worldwide' },
  Spanish: { code: 'ES', region: 'Spain, Latin America' },
  French: { code: 'FR', region: 'France' },
  Arabic: { code: 'AR', region: 'Middle East' },
  German: { code: 'DE', region: 'Germany' },
  Portuguese: { code: 'PT', region: 'Portugal, Brazil' },
  Chinese: { code: 'ZH', region: 'China' },
  'Chinese (Simplified)': { code: 'ZH', region: 'China' },
  'Chinese (Traditional)': { code: 'ZH', region: 'Taiwan' },
  Japanese: { code: 'JA', region: 'Japan' },
  Korean: { code: 'KO', region: 'Korea' },
  Hindi: { code: 'HI', region: 'India' },
  Italian: { code: 'IT', region: 'Italy' },
  Russian: { code: 'RU', region: 'Russia' },
  Dutch: { code: 'NL', region: 'Netherlands' },
  Polish: { code: 'PL', region: 'Poland' },
  Turkish: { code: 'TR', region: 'Turkey' },
  Vietnamese: { code: 'VI', region: 'Vietnam' },
  Thai: { code: 'TH', region: 'Thailand' },
  Swahili: { code: 'SW', region: 'East Africa' },
};

export function getLanguageLevelClassName(level: string): string {
  if (!level?.trim()) {
    return 'preview-spans preview-span-unset';
  }
  return LEVEL_CLASS_MAP[level] ?? 'preview-spans preview-span-unset';
}

export function hasCompleteLanguage(language: { language?: string; level?: string }): boolean {
  return Boolean(language.language?.trim() && language.level?.trim());
}

export function getLanguageMeta(languageName: string): {
  code: string;
  region: string;
} {
  const known = LANGUAGE_REGIONS[languageName];
  if (known) {
    return known;
  }

  const words = languageName.trim().split(/\s+/);
  const code =
    words.length > 1
      ? words
          .slice(0, 2)
          .map((word) => word[0])
          .join('')
          .toUpperCase()
      : languageName.slice(0, 2).toUpperCase();

  return { code, region: 'Worldwide' };
}

export function getLanguageLevelMeta(level: string) {
  if (level in LANGUAGE_LEVEL_META) {
    return LANGUAGE_LEVEL_META[level as LanguageLevel];
  }
  return null;
}
